import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~shared/shadcn/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { cn } from '~shared/shadcn/lib/utils';

import type { DashboardNavMenu } from './use-dashboard-nav-menus';
import type { NavItem } from './nav-section';

interface MobileNavSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menus: Array<DashboardNavMenu>;
}

const MobileNavLink = ({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) => {
  const className = cn(
    'flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700',
    'transition-colors hover:bg-slate-100 active:bg-slate-200',
  );

  const content = (
    <>
      <span className='flex size-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500'>
        <item.Icon className='size-4' strokeWidth={2} aria-hidden />
      </span>
      <span className='flex min-w-0 flex-1 flex-col text-left'>
        <span className='flex items-center gap-1.5'>
          <span>{item.label}</span>
          {item.href ? (
            <ExternalLink className='size-3 text-slate-400' aria-hidden />
          ) : null}
        </span>
        {item.description ? (
          <span className='text-xs font-normal text-slate-500'>
            {item.description}
          </span>
        ) : null}
      </span>
    </>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        target='_blank'
        rel='noopener noreferrer'
        className={className}
        onClick={onNavigate}
      >
        {content}
      </a>
    );
  }

  if (item.to) {
    return (
      <Link to={item.to} className={className} onClick={onNavigate}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type='button'
      className={cn(className, 'w-full')}
      onClick={() => {
        item.onClick?.();
        onNavigate();
      }}
    >
      {content}
    </button>
  );
};

export const MobileNavSheet = ({
  open,
  onOpenChange,
  menus,
}: MobileNavSheetProps) => {
  const close = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='left'
        className='w-[min(100vw-2rem,20rem)] overflow-y-auto p-0 sm:max-w-xs'
      >
        <SheetHeader className='border-b px-4 py-4 text-left'>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <Accordion type='multiple' className='px-2 py-2'>
          {menus.map((menu) =>
            menu.to && menu.sections.length === 0 ? (
              <div key={menu.sectionKey} className='border-b'>
                <Link
                  to={menu.to}
                  className='flex flex-1 items-center px-2 py-4 text-sm font-semibold transition-all hover:bg-slate-100'
                  onClick={close}
                >
                  {menu.label}
                </Link>
              </div>
            ) : (
              <AccordionItem key={menu.sectionKey} value={menu.sectionKey}>
                <AccordionTrigger className='px-2 text-sm font-semibold hover:no-underline'>
                  {menu.label}
                </AccordionTrigger>
                <AccordionContent className='pb-2'>
                  <div className='space-y-3'>
                    {menu.sections.map((section) => (
                      <div key={section.id}>
                        {section.label ? (
                          <p className='mb-1 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400'>
                            {section.label}
                          </p>
                        ) : null}
                        <div className='flex flex-col gap-0.5'>
                          {section.items.map((item) => (
                            <MobileNavLink
                              key={
                                item.id ??
                                item.to ??
                                item.href ??
                                item.label ??
                                'item'
                              }
                              item={item}
                              onNavigate={close}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ),
          )}
        </Accordion>
      </SheetContent>
    </Sheet>
  );
};
